import { addDoc, collection, doc, getDocs, query, serverTimestamp, where } from '@firebase/firestore';
import db from 'services/firebase'; 

interface StoryData {
  project_id: string | number;
  name: string;
  description?: string;
  story_type?: 'feature' | 'bug' | 'chore';
  priority?: 'urgent' | 'high' | 'medium' | 'normal' | 'low';
  dueDate?: string;
  status?: number; 
}

interface IntegrationData {
  apiToken: string;
  tenantId: string;
  uid: string;
  userName: string;
  email: string;
  is2FAEnabled?: boolean;
  projects?: any[]; 
  teams?: any[];
  workflows?: any[];
}

// Main integration function
const createShortcutIntegration = ({ apiToken }: { apiToken: string }) => {
  if (!apiToken || typeof apiToken !== 'string' || apiToken.trim() === '') {
    throw new Error('A valid API token is required for Shortcut integration');
  }

  // const baseURL = 'https://api.app.shortcut.com/api/v3';
  // const headers = {
  //   'Shortcut-Token': apiToken,
  //   'Content-Type': 'application/json',
  // };

  /**
   * Creates a story (ticket) in Shortcut
   * @param storyData - Data required to create the story
   * @param is2FAEnabled - Optional flag to indicate if user has 2FA enabled
   * @returns Success status and data or error message
   */
  const createStory = async (
    storyData: StoryData,
    is2FAEnabled?: boolean
  ): Promise<{ success: boolean; data?: any; error?: string }> => {
    try {
  
      const requestBody = {
        projectId: storyData.project_id,
        name: storyData.name,
        description: storyData.description || '',
        token: apiToken,
        storyType: storyData.story_type || 'feature',
        priority: storyData.priority || 'high',
        dueDate: storyData.dueDate || undefined,
        status: storyData.status || undefined,
      };
  
      const response = await fetch('https://us-central1-slate-134a0.cloudfunctions.net/shortcutCreateTask', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });
  
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create story');
      }
  
      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'An unexpected error occurred.';
      console.error('Error in createStory:', message);
      return { success: false, error: message };
    }
  };
  return {
    createStory,
  };
};

/**
 * Saves Shortcut integration details to Firebase Firestore
 * @param data - Integration data including API token and user info
 * @param currentUser - Current user object (for consistency with GitLab example)
 * @returns Success status and optional error message
 */
const saveShortcutIntegration = async (
  data: IntegrationData,
  currentUser: any
): Promise<{ success: boolean; error?: string }> => {
  try {
    const currentDate = new Date();
    const formattedDate = `${currentDate.getFullYear()}-${String(
      currentDate.getMonth() + 1
    ).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
    const todaysDate = `${currentDate.getMonth() + 1}/${currentDate.getDate()}/${currentDate.getFullYear()}`;

    const tenantRef = doc(db, 'tenants', data.tenantId);
    const integrationsRef = collection(tenantRef, 'integrations');
    const vendorsRef = collection(tenantRef, 'vendors');
    const integrationSnapshot = await getDocs(
      query(integrationsRef, where('name', '==', 'Shortcut'))
    );

    if (integrationSnapshot.empty) {
      const newData = {
        apiToken: data.apiToken,
        name: 'Shortcut',
        group: 'Project Management',
        img_path: '/media/integrations/Shortcut.avif',
        status: 'Connected',
        connectedAt: formattedDate,
        tenantId: data.tenantId,
        uid: data.uid,
        createdAt: serverTimestamp(),
        is2FAEnabled: data.is2FAEnabled ?? false,
        projects: data.projects || [],
        teams:data.teams || [],
      };

      const vendorData = {
        authentication_type: "API Token",
        category: "Project Management",
        created_at: serverTimestamp(),
        due_deligence_status: "Incomplete",
        impact_assessment: [],
        logo: [
          {
            name: "shortcut_logo.png",
            size: 3395,
            url: "/media/integrations/Shortcut.avif",
          },
        ],
        owner: currentUser?.userName || "",
        policy_url: "https://shortcut.com/privacy",
        reliance: "",
        risk_level: "",
        tenantId: currentUser?.tenantId,
        terms_url: "https://shortcut.com/terms",
        two_factor: true,
        uid: currentUser?.uid,
        updated_at: serverTimestamp(),
        vendor_name: "Shortcut",
        website_url: "https://shortcut.com",
      };

      await addDoc(integrationsRef, newData);
      await addDoc(vendorsRef, vendorData);

      const activitiesRef = collection(tenantRef, 'activities');
      await addDoc(activitiesRef, {
        message: `${data.userName} connected Shortcut integration`,
        timestamp: serverTimestamp(),
        tenantId: data.tenantId,
        uid: data.uid,
        action: 'Integration connected',
        target: 'Integrations',
        email: data.email,
        username: data.userName,
        date: todaysDate,
      });
    }

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

export { createShortcutIntegration, saveShortcutIntegration };